import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import './FullscreenPanel.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

/**
 * I pannelli montati, dal più vecchio all'ultimo aperto. All'Escape risponde solo
 * quello in cima: due pannelli sovrapposti — il profilo di un avversario aperto dal
 * profilo di un altro, per dirne una — si chiudevano tutti e due con un tasto solo,
 * perché ciascuno ascoltava per conto suo. Il gesto «indietro» non aveva il difetto:
 * la sua pila la tiene già [useOverlayBackButton], che chiude un pannello per volta.
 */
const pannelliAperti: object[] = [];

/**
 * C'e' un pannello a tutto schermo aperto adesso?
 *
 * Serve a chi ascolta l'Escape su `window` da PIU' IN BASSO nella pila e non ha
 * modo di saperlo: il lobby, per dirne uno, puo' avere sopra di se' il profilo di
 * un giocatore aperto dalla lista dei collegati che gli sta accanto. Senza questa
 * domanda un Escape chiudeva il profilo E il foglio sotto, cioe' due strati per un
 * tasto. La pila qui sopra la risposta la ha gia'; mancava solo la porta per
 * chiederla da fuori.
 *
 * È una FUNZIONE e non un valore di proposito: va chiamata nel gestore dell'evento,
 * dove la pila e' quella del momento, non al montaggio dell'effetto.
 */
export function cePannelloAperto(): boolean {
  return pannelliAperti.length > 0;
}

interface FullscreenPanelProps {
  title: string;
  /**
   * Un'azione resa SUBITO DOPO il titolo, prima della ×.
   *
   * Opzionale perche' nessuno dei cinque pannelli in produzione ne passa una: con
   * due figli la testata resta identica a com'era (vedi il commento sul margine
   * automatico in FullscreenPanel.css). La usa il pannello del profilo per la
   * bandierina «segnala», che appartiene a CHI si sta guardando — quindi sta
   * accanto al suo nome, non accanto alla chiusura.
   */
  azioneTitolo?: ReactNode;
  onClose: () => void;
  /** Accessible label for the close button. Defaults to English "Close". */
  closeLabel?: string;
  children: ReactNode;
}

/**
 * Full-screen overlay used on mobile to host the Moves / Chat panels that live
 * in the desktop sidebar. It keeps the game screen itself scroll-free: heavy
 * content opens here on demand instead of stretching the in-game column.
 * Closes on the X, a tap on the backdrop, or Escape; background scroll is
 * locked while open.
 */
export function FullscreenPanel({ title, azioneTitolo, onClose, closeLabel = 'Close', children }: FullscreenPanelProps) {
  /*
   * L'id del titolo e' GENERATO, non scritto a mano.
   *
   * Era la costante 'fs-panel-titolo', e due pannelli annidati — la classifica di
   * un torneo e, sopra, il profilo di un giocatore premuto da li' — producevano
   * due elementi con lo stesso id: il dialogo interno finiva etichettato dal
   * titolo di quello esterno, cioe' chi usa un lettore di schermo sentiva il nome
   * sbagliato. L'annidamento era gia' possibile (profilo sopra profilo); da oggi
   * si raggiunge da un posto in piu'.
   */
  const idTitolo = useId();
  // Mounted only while open, so the back gesture should always close it.
  useOverlayBackButton(true, onClose);

  // Lock background scroll for the whole lifetime of the overlay (independent of
  // onClose identity, so re-renders never toggle body overflow).
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Close on Escape — ma solo se questo è il pannello in cima alla pila. L'effetto
  // NON dipende dall'identità di `onClose` (un ri-render riordinerebbe la pila):
  // la chiusura passa da un ref.
  const chiudiRef = useRef(onClose);
  chiudiRef.current = onClose;
  useEffect(() => {
    const token = {};
    pannelliAperti.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (pannelliAperti[pannelliAperti.length - 1] !== token) return;
      chiudiRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const i = pannelliAperti.indexOf(token);
      if (i !== -1) pannelliAperti.splice(i, 1);
    };
  }, []);

  /*
   * Il fuoco dentro il pannello, Tab che non ne esce, e alla chiusura indietro a
   * chi l'aveva. Ruolo, aria-modal ed Escape c'erano già; il fuoco no — e questo
   * pannello copre l'intero schermo, quindi tabulare «dietro» vuol dire premere
   * bottoni completamente invisibili. Senza `onClose`: l'Escape è già suo.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(true);

  return createPortal(
    <div className="fs-panel-overlay" onClick={onClose}>
      <div
        ref={riferimentoDialogo}
        className="fs-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitolo}
        tabIndex={-1}
      >
        <div className="fs-panel-header">
          <span className="fs-panel-title" id={idTitolo}>{title}</span>
          {azioneTitolo}
          <button
            type="button"
            className="fs-panel-close"
            onClick={onClose}
            aria-label={closeLabel}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <div className="fs-panel-body">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export default FullscreenPanel;
